import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  InputAdornment,
  IconButton,
  TextField,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import {
  preprocessNames,
  getSuggestions,
} from "../../utils/searchSuggestions";

const NAMES_URL = "https://lebron-items.netlify.app/item_names.json";
const NO_RESULTS_PLACEHOLDER = "__NO_RESULTS_PLACEHOLDER__";

const SearchBar = ({ fullWidth = false, variant = "outlined", iconColor }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [indexedNames, setIndexedNames] = useState([]);
  const [namesLoaded, setNamesLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const maxSuggestions = useMemo(() => (isMobile ? 4 : 7), [isMobile]);

  useEffect(() => {
    let isMounted = true;

    const loadNames = async () => {
      try {
        const response = await fetch(NAMES_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load names: ${response.status}`);
        }

        const data = await response.json();
        const names = Array.isArray(data?.names) ? data.names : [];

        if (!isMounted) return;

        setIndexedNames(preprocessNames(names));
        setNamesLoaded(true);
      } catch (error) {
        console.error("Error loading names:", error);

        if (!isMounted) return;

        setIndexedNames([]);
        setNamesLoaded(true);
      }
    };

    loadNames();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith("/search/")) {
      setSearchTerm("");
      setOptions([]);
      setOpen(false);
      setSearchCompleted(false);
    }
  }, [location.pathname]);

  const color =
    iconColor ||
    (theme.palette.mode === "dark"
      ? theme.palette.primary.light
      : theme.palette.primary.main);

  const triggerSearch = (term = searchTerm) => {
    const trimmed = term.trim();
    if (!trimmed) return;

    setSearchTerm("");
    setOptions([]);
    setOpen(false);
    setSearchCompleted(false);

    navigate(`/search/${encodeURIComponent(trimmed)}`);
  };

  useEffect(() => {
    const trimmed = searchTerm.trim();

    if (trimmed.length < 3) {
      setOptions([]);
      setLoading(false);
      setSearchCompleted(false);
      return;
    }

    if (!namesLoaded) {
      setLoading(true);
      return;
    }

    const timeout = setTimeout(() => {
      setLoading(true);
      setSearchCompleted(false);

      try {
        const suggestions = getSuggestions({
          query: trimmed,
          indexedNames,
          maxSuggestions,
        });

        setOptions(suggestions);
        setSearchCompleted(true);
      } catch (error) {
        console.error("Error generating suggestions:", error);
        setOptions([]);
        setSearchCompleted(true);
      } finally {
        setLoading(false);
      }
    }, 150);

    return () => clearTimeout(timeout);
  }, [searchTerm, indexedNames, maxSuggestions, namesLoaded]);

  const displayOptions =
    searchCompleted &&
      !loading &&
      searchTerm.trim().length >= 3 &&
      options.length === 0
      ? [NO_RESULTS_PLACEHOLDER]
      : options;

  return (
    <Autocomplete
      disableClearable
      forcePopupIcon={false}
      freeSolo
      options={displayOptions}
      filterOptions={(x) => x}
      onOpen={() => {
        if (searchTerm.trim().length >= 3) {
          setOpen(true);
        }
      }}
      getOptionLabel={(option) =>
        option === NO_RESULTS_PLACEHOLDER ? "No suggestions found" : option
      }
      getOptionDisabled={(option) => option === NO_RESULTS_PLACEHOLDER}
      noOptionsText=""
      inputValue={searchTerm}
      onInputChange={(e, val, reason) => {
        setSearchTerm(val);
        if (reason === "input") {
          setOpen(val.trim().length >= 3);
        }
      }}
      onChange={(event, value) => {
        if (value && value !== NO_RESULTS_PLACEHOLDER) {
          triggerSearch(value);
        }
        setOpen(false);
      }}
      loading={loading}
      fullWidth={fullWidth}
      open={open}
      onClose={() => setOpen(false)}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size="small"
          autoComplete="off"
          placeholder="search reports"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              triggerSearch();
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={16} />}
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => triggerSearch()}
                    edge="end"
                    aria-label="search"
                    size="small"
                    disabled={searchTerm.trim() === ""}
                    sx={{
                      cursor: searchTerm.trim() === "" ? "default" : "pointer",
                    }}
                  >
                    <SearchIcon sx={{ color }} />
                  </IconButton>
                </InputAdornment>
              </>
            ),
          }}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            minWidth: 120,
            width: "100%",
          }}
        />
      )}
    />
  );
};

export default SearchBar;